import React, { useEffect, useRef, useState } from "react"
import { setLicenseKey } from "survey-core"
import * as SurveyCreatorCore from "survey-creator-core"
import { SurveyCreator, SurveyCreatorComponent } from "survey-creator-react"
import "survey-core/survey.i18n.js"
import "survey-creator-core/survey-creator-core.i18n.js"
import "survey-core/defaultV2.css"
import "survey-creator-core/survey-creator-core.css"
import "./IntakeFormsPage.sass"

import Breadcrumbs from "../components/shared/Breadcrumbs"
import Typography from "../components/shared/Typography"
import {
  PORTAL_AI_ASSISTANT_OPEN_REQUEST,
  PORTAL_AI_ASSISTANT_REFRESH_INTAKE_FORMS,
  portalAiIntakeFormGenerationPrompt
} from "../constants/portalAiAssistantEvents"
import GraphQLProvider from "../providers/GraphQLProvider"
import { urqlClient } from "../utils/utils"

import IntakeFormArchiveConfirmModal from "./IntakeForms/IntakeFormArchiveConfirmModal"
import IntakeFormNameModal from "./IntakeForms/IntakeFormNameModal"
import IntakeFormSettingsToolbar from "./IntakeForms/IntakeFormSettingsToolbar"

const questionTypes = [
  "boolean",
  "checkbox",
  "comment",
  "dropdown",
  "tagbox",
  "image",
  "matrix",
  "matrixdropdown",
  "matrixdynamic",
  "multipletext",
  "panel",
  "paneldynamic",
  "radiogroup",
  "rating",
  "ranking",
  "signaturepad",
  "text"
]

const UPSERT_INTAKE_FORM_MUTATION = `
  mutation($data: String!, $saveNo: Int!, $intakeFormId: ID, $name: String) {
    upsertIntakeForm(data: $data, saveNo: $saveNo, intakeFormId: $intakeFormId, name: $name) {
      result
      intakeFormId
    }
  }
`

const UPSERT_INTAKE_FORM_THEME_MUTATION = `
  mutation($data: String!) {
    upsertIntakeFormTheme(data: $data) {
      result
    }
  }
`

const enLocale = SurveyCreatorCore.localization.getLocale("en")
enLocale.ed.surveyTypeName = "Outline"

const ARCHIVE_INTAKE_FORM_MUTATION = `
  mutation($intakeFormId: ID!) {
    archiveIntakeForm(intakeFormId: $intakeFormId) {
      result
      errors
    }
  }
`

const SET_DEFAULT_INTAKE_FORM_MUTATION = `
  mutation($intakeFormId: ID!) {
    setDefaultIntakeForm(intakeFormId: $intakeFormId) {
      result
      errors
    }
  }
`

function openPortalAiForIntakeForm(intakeFormId) {
  window.dispatchEvent(
    new CustomEvent(PORTAL_AI_ASSISTANT_OPEN_REQUEST, {
      detail: {
        prompt: portalAiIntakeFormGenerationPrompt(intakeFormId),
        ...(intakeFormId ? { intakeFormId } : {})
      }
    })
  )
}

function IntakeForms({
  formJson,
  theme,
  intakeFormId: initialFormId,
  intakeFormName: initialFormName,
  intakeForms: initialForms,
  multipleIntakeFormsEnabled = true,
  portalAiAssistantEnabled = false,
  surveyJsLicenseKey
}) {
  const [nameModalOpen, setNameModalOpen] = useState(false)
  const [nameModalMode, setNameModalMode] = useState("new")
  const [nameModalInitialName, setNameModalInitialName] = useState("")
  const [nameModalLoading, setNameModalLoading] = useState(false)
  const [archiveModalOpen, setArchiveModalOpen] = useState(false)
  const [forms, setForms] = useState(initialForms || [])
  const creatorRef = useRef(null)

  if (surveyJsLicenseKey) {
    setLicenseKey(surveyJsLicenseKey)
  }

  const activeFormId = initialFormId
  const activeFormName =
    initialFormName || forms.find((form) => String(form.id) === String(activeFormId))?.name || "Intake form"

  const handleSelectForm = (formId) => {
    window.location.href = `/portal/settings/intake_forms?id=${formId}`
  }

  const openNewFormModal = () => {
    setNameModalMode("new")
    setNameModalInitialName("")
    setNameModalOpen(true)
  }

  const openRenameModal = () => {
    setNameModalMode("rename")
    setNameModalInitialName(activeFormName)
    setNameModalOpen(true)
  }

  const handleNameModalSubmit = (name) => {
    setNameModalLoading(true)

    if (nameModalMode === "new") {
      const defaultFormJson = JSON.stringify({ pages: [{ name: "page1", elements: [] }] })
      urqlClient
        .mutation(UPSERT_INTAKE_FORM_MUTATION, { data: defaultFormJson, saveNo: 0, name })
        .toPromise()
        .then((result) => {
          if (result.data?.upsertIntakeForm?.intakeFormId) {
            window.location.href = `/portal/settings/intake_forms?id=${result.data.upsertIntakeForm.intakeFormId}`
          } else {
            setNameModalLoading(false)
          }
        })
        .catch(() => setNameModalLoading(false))
      return
    }

    urqlClient
      .mutation(UPSERT_INTAKE_FORM_MUTATION, {
        data: JSON.stringify(creator.JSON),
        saveNo: creator.JSON.saveNo || 0,
        intakeFormId: activeFormId,
        name
      })
      .toPromise()
      .then((result) => {
        if (result.data?.upsertIntakeForm?.result === "success") {
          window.location.reload()
        } else {
          setNameModalLoading(false)
        }
      })
      .catch(() => setNameModalLoading(false))
  }

  const handleSetDefault = () => {
    urqlClient
      .mutation(SET_DEFAULT_INTAKE_FORM_MUTATION, { intakeFormId: activeFormId })
      .toPromise()
      .then((result) => {
        if (result.data?.setDefaultIntakeForm?.result === "success") {
          window.location.reload()
        } else {
          window.alert(result.data?.setDefaultIntakeForm?.errors?.join(", ") || "Error setting default")
        }
      })
  }

  const handleArchiveConfirm = async () => {
    const result = await urqlClient.mutation(ARCHIVE_INTAKE_FORM_MUTATION, { intakeFormId: activeFormId }).toPromise()

    if (result.data?.archiveIntakeForm?.result === "success") {
      window.location.href = "/portal/settings/intake_forms"
      return
    }

    setArchiveModalOpen(false)
    window.alert(result.data?.archiveIntakeForm?.errors?.join(", ") || "Error archiving form")
  }

  const options = {
    showLogicTab: false,
    showJSONEditorTab: false,
    showThemeTab: true,
    questionTypes
  }
  const creator = new SurveyCreator(options)
  creatorRef.current = creator

  const whiteList = [
    "title",
    "description",
    "isRequired",
    "showCommentArea",
    "commentText",
    "commentPlaceholder",
    "labelTrue",
    "labelFalse",
    "swapOrder",
    "choices",
    "choicesFromQuestion",
    "choicesFromQuestionMode",
    "choiceValuesFromQuestion",
    "choiceTextsFromQuestion",
    "choicesOrder",
    "items",
    "itemSize",
    "colCount",
    "allowAddRemove",
    "allowRemoveAll",
    "columns",
    "rows"
  ]

  creator.onShowingProperty.add(function (_, options) {
    if (options.obj.constructor.name !== "ThemeModel" && options.obj.constructor.name !== "HeaderModel") {
      options.canShow = whiteList.indexOf(options.property.name) > -1
    }
  })

  creator.onQuestionAdded.add(function (sender, options) {
    if (options.question.getType() === "matrix") {
      options.question.allowAddRemove = true
      options.question.allowRemoveAll = true
      options.question.itemSize = 0

      if (options.question.rows && options.question.rows.length > 0) {
        options.question.rows.forEach((row) => {
          if (typeof row === "object" && row.value) {
            row.allowRemove = true
          }
        })
      }
    }
  })

  creator.onPropertyGridSurveyCreated.add(function (sender, options) {
    const survey = options.survey
    survey.onGetQuestionTitle.add(function (sender, options) {
      if (options.question.getType() === "matrix") {
        const question = options.question
        if (question.allowAddRemove === undefined) {
          question.allowAddRemove = true
        }
        if (question.allowRemoveAll === undefined) {
          question.allowRemoveAll = true
        }
      }
    })
  })

  creator.isAutoSave = true
  creator.showState = true
  creator.JSON = JSON.parse(formJson)
  creator.theme = JSON.parse(theme)

  setTimeout(() => {
    if (creator.survey) {
      creator.survey.getAllQuestions().forEach((question) => {
        if (question.getType() === "matrix") {
          question.allowAddRemove = true
          question.allowRemoveAll = true
          question.itemSize = 0
        }
      })
    }
  }, 1000)

  creator.onSurveyInstanceCreated.add(function (sender, options) {
    const survey = options.survey
    survey.getAllQuestions().forEach((question) => {
      if (question.getType() === "matrix") {
        question.allowAddRemove = true
        question.allowRemoveAll = true
        question.itemSize = 0

        if (question.rows && question.rows.length > 0) {
          question.rows.forEach((row) => {
            if (typeof row === "object" && row.value) {
              row.allowRemove = true
            }
          })
        }
      }
    })
  })

  creator.onSurveyInstanceCreated.add(function (sender, options) {
    const survey = options.survey
    if (survey) {
      survey.getAllQuestions().forEach((question) => {
        if (question.getType() === "matrix") {
          question.allowAddRemove = true
          question.allowRemoveAll = true
          question.itemSize = 0
        }
      })
    }
  })

  creator.toolbox.forceCompact = false
  creator.saveSurveyFunc = (saveNo, callback) => {
    urqlClient
      .mutation(UPSERT_INTAKE_FORM_MUTATION, { data: JSON.stringify(creator.JSON), saveNo, intakeFormId: activeFormId })
      .toPromise()
      .then((result) => {
        if (result.error) {
          console.error(result) // eslint-disable-line no-console
          callback(saveNo, false)
        } else {
          callback(saveNo, true)
        }
      })
  }

  creator.saveThemeFunc = (saveNo, callback) => {
    urqlClient
      .mutation(UPSERT_INTAKE_FORM_THEME_MUTATION, { data: JSON.stringify(creator.theme), saveNo })
      .toPromise()
      .then((result) => {
        if (result.error) {
          console.error(result) // eslint-disable-line no-console
          callback(saveNo, false)
        } else {
          callback(saveNo, true)
        }
      })
  }

  useEffect(() => {
    const onRefresh = async () => {
      const params = new URLSearchParams(window.location.search)
      const id = params.get("id")
      const refreshUrl = id
        ? `/portal/settings/intake_forms/refresh?id=${encodeURIComponent(id)}`
        : "/portal/settings/intake_forms/refresh"
      try {
        const res = await fetch(refreshUrl, {
          credentials: "same-origin",
          headers: { Accept: "application/json" }
        })
        if (!res.ok) return
        const json = await res.json()
        setForms(json.intakeForms || [])
        if (String(json.intakeFormId) !== String(activeFormId)) {
          window.location.assign(`/portal/settings/intake_forms?id=${json.intakeFormId}`)
          return
        }
        const activeCreator = creatorRef.current
        if (activeCreator && json.formJson) {
          try {
            activeCreator.JSON = JSON.parse(json.formJson)
          } catch {
            window.location.assign(`/portal/settings/intake_forms?id=${json.intakeFormId}`)
          }
        }
      } catch {
        /* ignore */
      }
    }
    window.addEventListener(PORTAL_AI_ASSISTANT_REFRESH_INTAKE_FORMS, onRefresh)
    return () => window.removeEventListener(PORTAL_AI_ASSISTANT_REFRESH_INTAKE_FORMS, onRefresh)
  }, [activeFormId])

  const intakeFormLabel = multipleIntakeFormsEnabled ? "Intake Forms" : "Intake Form"
  const intakeFormSubtitle = multipleIntakeFormsEnabled
    ? "Create and manage intake forms to send to your clients"
    : "Create and manage the intake form that you send to your clients"
  const helpText = `Download a blank PDF for in-person use, or print the form template.${
    portalAiAssistantEnabled
      ? " Use Generate with AI for a new form from a PDF/Word doc, or Update form with AI to replace the selected form."
      : ""
  }`

  return (
    <div className="h-[calc(100vh-156px)]">
      <div className="flex flex-col">
        <Breadcrumbs links={[{ href: "/portal/settings", text: "Settings" }]} activePage={intakeFormLabel} />
        <Typography as="h1" variant="h3">
          {intakeFormLabel}
        </Typography>
        <Typography variant="subtitle">{intakeFormSubtitle}</Typography>
        <Typography variant="micro" className="mt-1 text-gray-dark">
          {helpText}
        </Typography>
      </div>

      <IntakeFormSettingsToolbar
        intakeForms={forms}
        activeFormId={activeFormId}
        multipleIntakeFormsEnabled={multipleIntakeFormsEnabled}
        portalAiAssistantEnabled={portalAiAssistantEnabled}
        formName={activeFormName}
        licenseKey={surveyJsLicenseKey}
        getSurveyJson={() => creator.JSON}
        onSelectForm={handleSelectForm}
        onNewForm={openNewFormModal}
        onRename={openRenameModal}
        onSetDefault={handleSetDefault}
        onArchive={() => setArchiveModalOpen(true)}
        onGenerateWithAi={() => openPortalAiForIntakeForm(null)}
        onUpdateWithAi={() => openPortalAiForIntakeForm(activeFormId)}
      />

      <SurveyCreatorComponent creator={creator} />

      <IntakeFormNameModal
        visible={nameModalOpen}
        title={nameModalMode === "new" ? "New form" : "Rename form"}
        initialName={nameModalInitialName}
        saveLabel={nameModalMode === "new" ? "Create form" : "Save"}
        loading={nameModalLoading}
        onClose={() => {
          if (!nameModalLoading) setNameModalOpen(false)
        }}
        onSubmit={handleNameModalSubmit}
      />

      <IntakeFormArchiveConfirmModal
        visible={archiveModalOpen}
        formName={activeFormName}
        onClose={() => setArchiveModalOpen(false)}
        onConfirm={handleArchiveConfirm}
      />
    </div>
  )
}

export default function IntakeFormsPage(props) {
  return (
    <GraphQLProvider>
      <IntakeForms {...props} multipleIntakeFormsEnabled={props.multipleIntakeFormsEnabled !== false} />
    </GraphQLProvider>
  )
}
